import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../_core/_services/auth.service';
import { NotificationService } from '../../../_core/_services/notification.service';
import { SupportService } from '../../../_core/_services/support/support.service';
import { SupportChatResponse } from '../../../_core/_model/support/support';
import { MiniUserDto } from '../../../_core/_model/users';

interface SupportWidgetMessage {
  sender: 'user' | 'bot' | 'system';
  label: string;
  text: string;
  at: Date;
}

@Component({
  selector: 'app-support-widget',
  standalone: false,
  templateUrl: './support-widget.html',
  styleUrl: './support-widget.css'
})
export class SupportWidget implements OnInit, OnDestroy, AfterViewInit {
  opened = false;
  sending = false;
  creatingTicket = false;
  requestingReactivation = false;
  reactivationRequested = false;

  input = '';
  sessionKey: string | null = null;
  latestChatResponse: SupportChatResponse | null = null;

  reactivationEmail = '';
  reactivationReason = '';

  isLoggedIn = false;
  currentUser: MiniUserDto | null = null;

  userLabel = 'You';
  readonly botLabel = 'SwipFx';

  private subs = new Subscription();

  messages: SupportWidgetMessage[] = [
    {
      sender: 'bot',
      label: 'SwipFx',
      text: 'Hi. I can help with accounts, security, multiple payment channels, deposits, withdrawals, Instant and Flexible trading, rates, additional documents, and compliance questions.',
      at: new Date()
    }
  ];

  get quickTopics(): string[] {
    return this.isLoggedIn
      ? [
          'Which deposit channels are available to me?',
          'Explain Instant versus Flexible rates',
          'What is my latest deposit status?',
          'What is my latest order status?',
          'Do I have an additional document request?'
        ]
      : [
          'What payment methods does SwipFx support?',
          'Explain Instant versus Flexible rates',
          'How does Interac e-Transfer work?',
          'What is a reusable virtual account?',
          'How do I reset my password?'
        ];
  }

  constructor(
    private authService: AuthService,
    private supportService: SupportService,
    private notifyService: NotificationService
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.authService.isLoggedIn$.subscribe(value => {
        this.isLoggedIn = value;
      })
    );

    this.subs.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUser = user;
        this.userLabel = this.getFirstName(user) || 'You';
      })
    );

    this.authService.getCurrentUser().subscribe();
  }

  ngAfterViewInit(): void {
    window.addEventListener('swipfx-support-open', this.handleExternalSupportOpen as EventListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('swipfx-support-open', this.handleExternalSupportOpen as EventListener);
    this.subs.unsubscribe();
  }

  toggle(): void {
    this.opened = !this.opened;
  }

  close(): void {
    this.opened = false;
  }

  send(): void {
    const message = this.input.trim();
    if (!message || this.sending) return;

    this.messages.push({
      sender: 'user',
      label: this.userLabel,
      text: message,
      at: new Date()
    });

    this.input = '';
    this.sending = true;
    this.latestChatResponse = null;

    const payload = {
      message,
      sessionKey: this.sessionKey,
      intentHint: null
    };

    const request$ = this.isLoggedIn
      ? this.supportService.chatAuthenticated(payload)
      : this.supportService.chatPublic(payload);

    request$.subscribe({
      next: (res) => {
        const data = res.data;
        if (!data) {
          this.messages.push({
            sender: 'system',
            label: 'System',
            text: 'No response was returned from support.',
            at: new Date()
          });
          return;
        }

        this.sessionKey = data.sessionKey;
        this.latestChatResponse = data;

        this.messages.push({
          sender: 'bot',
          label: this.botLabel,
          text: data.reply,
          at: new Date()
        });
      },
      error: (err) => {
        this.messages.push({
          sender: 'system',
          label: 'System',
          text: err?.error?.message || 'Support is unavailable right now.',
          at: new Date()
        });
        this.sending = false;
      },
      complete: () => {
        this.sending = false;
      }
    });
  }

  askQuickQuestion(question: string): void {
    if (this.sending) return;
    this.input = question;
    this.send();
  }

  submitReactivationRequest(): void {
    const email = this.reactivationEmail.trim();
    const reason = this.reactivationReason.trim();

    if (!email || !this.isValidEmail(email)) {
      this.notifyService.showError('Enter a valid account email address.', 'Account Reactivation');
      return;
    }

    if (this.requestingReactivation) return;

    this.requestingReactivation = true;
    this.supportService.requestAccountReactivation({
      email,
      reason: reason || null,
      sessionKey: this.sessionKey
    }).subscribe({
      next: (res) => {
        const message = res.data?.message || res.message ||
          'If an eligible deactivated account exists, a verification link has been sent.';

        this.reactivationRequested = true;
        this.reactivationReason = '';
        this.messages.push({
          sender: 'system',
          label: 'System',
          text: message,
          at: new Date()
        });
        this.notifyService.showSuccess(message, 'Account Reactivation');
      },
      error: (err) => {
        const message = err?.error?.message || 'Unable to submit the reactivation request.';
        this.messages.push({
          sender: 'system',
          label: 'System',
          text: message,
          at: new Date()
        });
        this.notifyService.showError(message, 'Account Reactivation');
        this.requestingReactivation = false;
      },
      complete: () => {
        this.requestingReactivation = false;
      }
    });
  }

  shouldShowReactivationForm(): boolean {
    return !this.isLoggedIn &&
      this.latestChatResponse?.suggestedAction === 'RequestAccountReactivation';
  }

  hasSuggestedRoute(): boolean {
    return !!this.latestChatResponse?.suggestedActionRoute &&
      this.latestChatResponse?.suggestedAction !== 'RequestAccountReactivation';
  }

  createTicketFromLatestChat(): void {
    if (!this.latestChatResponse || this.creatingTicket) return;

    const lastUserMessage = [...this.messages]
      .reverse()
      .find(x => x.sender === 'user')
      ?.text
      ?.trim();

    if (!lastUserMessage) {
      this.notifyService.showError('No chat message found to turn into a ticket.', 'Error');
      return;
    }

    const subject = this.buildTicketSubject(this.latestChatResponse, lastUserMessage);

    this.creatingTicket = true;

    this.supportService
      .createTicketFromChat(subject, lastUserMessage, this.latestChatResponse)
      .subscribe({
        next: (res) => {
          const ticket = res.data;
          this.notifyService.showSuccess(
            res.message || 'Support ticket created successfully.',
            'Success'
          );

          this.messages.push({
            sender: 'system',
            label: 'System',
            text: ticket
              ? `Ticket created successfully: ${ticket.subject}`
              : 'Support ticket created successfully.',
            at: new Date()
          });
        },
        error: (err) => {
          this.notifyService.showError(
            err?.error?.message || 'Failed to create support ticket.',
            'Error'
          );
          this.creatingTicket = false;
        },
        complete: () => {
          this.creatingTicket = false;
        }
      });
  }

  clearConversation(): void {
    this.sessionKey = null;
    this.latestChatResponse = null;
    this.input = '';
    this.reactivationEmail = '';
    this.reactivationReason = '';
    this.reactivationRequested = false;
    this.messages = [
      {
        sender: 'bot',
        label: this.botLabel,
        text: 'New conversation started. Ask me about accounts, security, payment channels, deposits, withdrawals, Instant or Flexible trading, rates, additional documents, KYC, or general help.',
        at: new Date()
      }
    ];
  }

  canCreateTicket(): boolean {
    return !!this.isLoggedIn
      && !!this.latestChatResponse
      && !!this.latestChatResponse.suggestedTicketCreation;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private buildTicketSubject(chat: SupportChatResponse, userMessage: string): string {
    const prefix = chat.suggestedCategory || 'General';
    const trimmed = userMessage.length > 60
      ? `${userMessage.slice(0, 60)}...`
      : userMessage;

    return `${prefix}: ${trimmed}`;
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private getFirstName(user: MiniUserDto | null): string {
    if (!user) return '';

    const fullName =
      (user as any).fullName ||
      (user as any).name ||
      (user as any).displayName ||
      '';

    if (!fullName) return '';

    return fullName.trim().split(/\s+/)[0] || '';
  }

  private handleExternalSupportOpen = (event: Event): void => {
    const customEvent = event as CustomEvent<{ question?: string; email?: string }>;
    const question = customEvent.detail?.question?.trim();
    const email = customEvent.detail?.email?.trim();

    this.opened = true;

    if (question) {
      this.input = question;
      setTimeout(() => this.send());
    }

    if (email) {
      this.reactivationEmail = email;
    }
  };
}